/**
 * EAChatDrawer Component - Institutional Grade Q.AI Chat Panel
 *
 * A slide-out drawer that provides contextual Q.AI chat for EA analysis.
 * Follows the design patterns from QChatWindow, QChatMessages, and QChatInput.
 *
 * Features:
 * - Slide-in from right edge (500px width)
 * - Dark theme matching Q.AI aesthetic (#0A0A0F)
 * - Context-aware: Injects EA analysis into prompts
 * - Suggested questions based on analysis results
 * - Real-time streaming responses
 * - Conversation persistence
 *
 * Usage:
 * <EAChatDrawer
 *   open={drawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 *   token={token}
 *   file={selectedFile}
 *   analysisContext={analysisContext}
 * />
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Chip,
  Fade,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CodeIcon from '@mui/icons-material/Code';
import QChatMessages from '../QChatMessages';
import QChatInput from '../QChatInput';
import useQChat from '../../../hooks/useQChat';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const DRAWER_WIDTH = 500;

const DRAWER_COLORS = {
  background: '#0A0A0F',
  surface: '#111116',
  border: 'rgba(255, 255, 255, 0.06)',
  borderHover: 'rgba(255, 255, 255, 0.12)',
  text: {
    primary: '#F1F5F9',
    secondary: 'rgba(255, 255, 255, 0.6)',
    muted: 'rgba(255, 255, 255, 0.4)'
  },
  accent: {
    primary: '#27B56D',
    secondary: '#7c3aed',
    gradient: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)'
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// SUGGESTED QUESTIONS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SuggestedQuestions - Quick action chips based on analysis context
 */
const SuggestedQuestions = ({ questions, onSelect, disabled }) => {
  if (!questions || questions.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        px: '16px',
        py: '12px',
        borderTop: `1px solid ${DRAWER_COLORS.border}`
      }}
    >
      <Typography
        sx={{
          width: '100%',
          fontSize: '0.6875rem',
          fontWeight: 600,
          color: DRAWER_COLORS.text.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          mb: '4px'
        }}
      >
        Suggested Questions
      </Typography>
      {questions.map((q) => (
        <Chip
          key={q.id}
          label={q.label}
          onClick={() => onSelect(q.prompt)}
          disabled={disabled}
          size="small"
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.08)',
            color: DRAWER_COLORS.text.secondary,
            border: '1px solid rgba(124, 58, 237, 0.2)',
            fontSize: '0.75rem',
            fontWeight: 500,
            height: '28px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.15)',
              borderColor: 'rgba(124, 58, 237, 0.4)',
              color: DRAWER_COLORS.text.primary
            },
            '&.Mui-disabled': {
              opacity: 0.5,
              cursor: 'not-allowed'
            }
          }}
        />
      ))}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT BANNER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ContextBanner - Shows current EA context at top of drawer
 */
const ContextBanner = ({ fileName, hasAnalysis, qualityScore }) => {
  if (!fileName) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        px: '16px',
        py: '10px',
        backgroundColor: hasAnalysis
          ? 'rgba(39, 181, 109, 0.06)'
          : 'rgba(124, 58, 237, 0.06)',
        borderBottom: `1px solid ${DRAWER_COLORS.border}`
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '6px',
          backgroundColor: hasAnalysis
            ? 'rgba(39, 181, 109, 0.15)'
            : 'rgba(124, 58, 237, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <CodeIcon
          sx={{
            fontSize: '0.875rem',
            color: hasAnalysis ? DRAWER_COLORS.accent.primary : DRAWER_COLORS.accent.secondary
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: DRAWER_COLORS.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: "'JetBrains Mono', monospace"
          }}
        >
          {fileName}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            color: DRAWER_COLORS.text.muted
          }}
        >
          {hasAnalysis ? 'Analysis complete' : 'Ready to analyze'}
        </Typography>
      </Box>
      {hasAnalysis && qualityScore !== undefined && (
        <Box
          sx={{
            px: '8px',
            py: '4px',
            borderRadius: '4px',
            backgroundColor: qualityScore >= 70
              ? 'rgba(39, 181, 109, 0.15)'
              : qualityScore >= 40
                ? 'rgba(234, 179, 8, 0.15)'
                : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${
              qualityScore >= 70
                ? 'rgba(39, 181, 109, 0.3)'
                : qualityScore >= 40
                  ? 'rgba(234, 179, 8, 0.3)'
                  : 'rgba(239, 68, 68, 0.3)'
            }`
          }}
        >
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: qualityScore >= 70
                ? '#27B56D'
                : qualityScore >= 40
                  ? '#EAB308'
                  : '#EF4444'
            }}
          >
            {qualityScore}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DRAWER HEADER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * DrawerHeader - Title bar with close and reset actions
 */
const DrawerHeader = ({ onClose, onReset, hasMessages }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '16px',
        py: '12px',
        borderBottom: `1px solid ${DRAWER_COLORS.border}`,
        backgroundColor: DRAWER_COLORS.surface
      }}
    >
      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: DRAWER_COLORS.accent.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(39, 181, 109, 0.25)'
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontWeight: 800,
              fontFamily: "'Inter', sans-serif"
            }}
          >
            Q
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 700,
              color: DRAWER_COLORS.text.primary,
              letterSpacing: '-0.01em'
            }}
          >
            Ask Q.AI
          </Typography>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              color: DRAWER_COLORS.text.muted
            }}
          >
            EA Code Assistant
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {hasMessages && (
          <Tooltip title="Start new conversation" arrow placement="bottom">
            <IconButton
              onClick={onReset}
              size="small"
              sx={{
                color: DRAWER_COLORS.text.muted,
                '&:hover': {
                  color: DRAWER_COLORS.text.primary,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              <RestartAltIcon sx={{ fontSize: '1.125rem' }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close" arrow placement="bottom">
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: DRAWER_COLORS.text.muted,
              '&:hover': {
                color: DRAWER_COLORS.text.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * EAChatDrawer - Slide-out panel with embedded Q.AI chat
 *
 * @param {boolean} open - Whether drawer is visible
 * @param {function} onClose - Handler to close drawer
 * @param {string} token - JWT authentication token
 * @param {object} file - Currently selected EA file
 * @param {object} analysisContext - Analysis results for context injection
 * @param {string} initialPrompt - Optional initial message to send
 */
const EAChatDrawer = ({
  open,
  onClose,
  token,
  file,
  analysisContext,
  initialPrompt
}) => {
  const inputRef = useRef(null);

  // Initialize chat hook
  const {
    messages,
    balance,
    isLoading,
    error,
    sendMessage,
    handleActionChip,
    cancelStream,
    clearMessages,
    hasConversation,
    refreshBalance
  } = useQChat(token);

  /**
   * Fetch balance when drawer opens
   */
  useEffect(() => {
    if (open && token && balance === null) {
      console.log('[EAChatDrawer] Fetching balance on drawer open');
      refreshBalance();
    }
  }, [open, token, balance, refreshBalance]);

  /**
   * Build context-aware prompt with minimal EA context hint
   *
   * P8-D ARCHITECTURE (Backend-Driven Context):
   * ============================================
   * The FIX Engine backend handles full context injection:
   * 1. QAIController detects IntentType.EAAssistant from message pattern [EA: filename]
   * 2. Fetches analysis + source code from EAAnalysisCacheService (P8-B L1 cache)
   * 3. QAIService.BuildSystemPrompt injects context with smart truncation
   * 4. TokenBudgetManager applies 3000-token limit preserving important sections
   *
   * Frontend responsibility:
   * - Send user's question with minimal context hint (fileName)
   * - DO NOT send full analysis or source code (wasteful, duplicative)
   * - Backend will fetch and inject the complete context automatically
   */
  const buildContextualPrompt = useCallback((userMessage) => {
    if (!analysisContext) return userMessage;

    const { fileName } = analysisContext;

    // Only include minimal context hint for backend intent detection
    // Format: [EA: filename] triggers EAAssistant intent in QAIController
    // Backend P8-D will fetch and inject full analysis + truncated source code
    if (fileName) {
      return `[EA: ${fileName}] ${userMessage}`;
    }

    return userMessage;
  }, [analysisContext]);

  /**
   * Handle sending message with context injection
   */
  const handleSend = useCallback((message) => {
    console.log('[EAChatDrawer] handleSend called with:', message);
    console.log('[EAChatDrawer] Current balance:', balance, 'isLoading:', isLoading);
    console.log('[EAChatDrawer] analysisContext:', JSON.stringify(analysisContext, null, 2));

    if (balance === null) {
      console.warn('[EAChatDrawer] Cannot send - balance is null (still loading)');
      return;
    }
    if (balance === 0) {
      console.warn('[EAChatDrawer] Cannot send - no tokens');
      return;
    }

    const contextualMessage = buildContextualPrompt(message);
    console.log('[EAChatDrawer] Sending contextual message:', contextualMessage.substring(0, 100) + '...');
    sendMessage(contextualMessage, 'ea-chat');
  }, [sendMessage, buildContextualPrompt, balance, isLoading]);

  /**
   * Handle suggested question click
   */
  const handleSuggestedQuestion = useCallback((prompt) => {
    console.log('[EAChatDrawer] Suggested question clicked:', prompt);
    console.log('[EAChatDrawer] Balance:', balance, 'isLoading:', isLoading);
    handleSend(prompt);
  }, [handleSend, balance, isLoading]);

  /**
   * Handle action chip click from welcome message
   */
  const handleChipClick = useCallback((action) => {
    if (action?.prompt) {
      handleSend(action.prompt);
    }
  }, [handleSend]);

  /**
   * Generate suggested questions based on analysis context
   */
  const suggestedQuestions = React.useMemo(() => {
    if (!analysisContext || !file) {
      return [
        { id: 'general', label: 'About EAs', prompt: 'What are the best practices for writing Expert Advisors in MQL5?' }
      ];
    }

    const { bugs, quality, improvements, riskFactors } = analysisContext;
    const questions = [];

    // Summary question
    questions.push({
      id: 'summary',
      label: 'Explain results',
      prompt: `Can you explain the analysis results for "${file.name}" in simple terms?`
    });

    // Bug fix question
    if (bugs?.length > 0) {
      questions.push({
        id: 'bugs',
        label: `Fix bugs (${bugs.length})`,
        prompt: `How do I fix the ${bugs.length} bug(s) found in "${file.name}"? Start with the most critical.`
      });
    }

    // Quality improvement
    if (quality?.overallScore && quality.overallScore < 70) {
      questions.push({
        id: 'quality',
        label: 'Improve quality',
        prompt: `What changes would improve the code quality score of "${file.name}" the most?`
      });
    }

    // Risk mitigation
    if (riskFactors?.length > 0) {
      questions.push({
        id: 'risks',
        label: 'Reduce risks',
        prompt: `What are the risk factors in "${file.name}" and how can I mitigate them?`
      });
    }

    // Improvements
    if (improvements?.length > 0 && questions.length < 4) {
      questions.push({
        id: 'improve',
        label: 'Suggested fixes',
        prompt: `What are the most important improvements I should make to "${file.name}"?`
      });
    }

    return questions.slice(0, 4);
  }, [analysisContext, file]);

  // Track if initial prompt has been sent to prevent re-sending
  const initialPromptSentRef = useRef(false);

  /**
   * Reset sent flag when drawer closes or initialPrompt changes
   */
  useEffect(() => {
    if (!open) {
      initialPromptSentRef.current = false;
    }
  }, [open]);

  /**
   * Send initial prompt ONCE when drawer opens and balance is ready
   */
  useEffect(() => {
    // Only send if: drawer open, have initial prompt, not already sent, balance loaded, not loading
    if (
      open &&
      initialPrompt &&
      !initialPromptSentRef.current &&
      balance !== null &&
      balance > 0 &&
      !isLoading
    ) {
      console.log('[EAChatDrawer] Sending initial prompt (once):', initialPrompt.substring(0, 50) + '...');
      initialPromptSentRef.current = true; // Mark as sent BEFORE sending

      const timer = setTimeout(() => {
        handleSend(initialPrompt);
      }, 300); // Wait for drawer animation

      return () => clearTimeout(timer);
    }
  }, [open, initialPrompt, balance, isLoading]); // Removed handleSend from deps to prevent re-triggers

  /**
   * Focus input when drawer opens
   */
  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Determine if we should show suggested questions
  // Show when: drawer open, have context, not loading, and minimal messages
  const showSuggestions = open &&
    analysisContext &&
    !isLoading &&
    messages.length <= 2; // Only show for welcome + 0-1 messages

  // Check if we have actual conversation messages (beyond welcome)
  const hasRealMessages = messages.length > 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          backgroundColor: DRAWER_COLORS.background,
          borderLeft: `1px solid ${DRAWER_COLORS.border}`,
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.4)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DrawerHeader
        onClose={onClose}
        onReset={clearMessages}
        hasMessages={hasRealMessages}
      />

      {/* Context Banner */}
      <ContextBanner
        fileName={file?.name}
        hasAnalysis={!!analysisContext}
        qualityScore={analysisContext?.quality?.overallScore}
      />

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: DRAWER_COLORS.background
        }}
      >
        <QChatMessages
          messages={messages}
          onActionCard={() => {}}
          onActionChip={handleChipClick}
          isLoading={isLoading}
          hasConversation={hasConversation}
        />
      </Box>

      {/* Suggested Questions */}
      <Fade in={showSuggestions} timeout={200}>
        <Box>
          {showSuggestions && (
            <SuggestedQuestions
              questions={suggestedQuestions}
              onSelect={handleSuggestedQuestion}
              disabled={isLoading || balance === null || balance === 0}
            />
          )}
        </Box>
      </Fade>

      {/* Input Area */}
      <Box
        sx={{
          px: '12px',
          py: '12px',
          borderTop: `1px solid ${DRAWER_COLORS.border}`,
          backgroundColor: DRAWER_COLORS.background
        }}
      >
        {/* Balance indicator */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: '8px'
          }}
        >
          <Typography
            sx={{
              fontSize: '0.625rem',
              color: balance === null
                ? DRAWER_COLORS.text.muted
                : balance > 0
                  ? DRAWER_COLORS.text.muted
                  : '#EF4444',
              fontWeight: 500
            }}
          >
            {balance === null
              ? 'Loading...'
              : balance > 0
                ? `${balance.toLocaleString()} tokens`
                : 'No tokens'}
          </Typography>
        </Box>

        <QChatInput
          ref={inputRef}
          onSend={handleSend}
          onCancel={cancelStream}
          isLoading={isLoading || balance === null}
          disabled={balance === 0}
          placeholder={balance === null
            ? 'Loading balance...'
            : file
              ? `Ask about ${file.name}...`
              : 'Ask Q.AI...'}
        />

        {/* Error display */}
        {error && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#EF4444',
              mt: '8px',
              textAlign: 'center'
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default EAChatDrawer;
